import React, { useEffect, useState } from 'react';
import { callsApi } from '../services/callsApi';
import { dataSyncApi, SyncStatus } from '../services/dataSyncApi';
import { Call, CallDetailResponse } from '../types';
import { useTheme } from '../context/ThemeContext';
import { useDebounce } from '../hooks/useDebounce';

const Calls: React.FC = () => {
  const [calls, setCalls] = useState<Call[]>([]);

  // Additional safety wrapper for setCalls to ensure it's never undefined
  const safeSetCalls = (newCalls: Call[] | undefined | null) => {
    setCalls(Array.isArray(newCalls) ? newCalls : []);
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCall, setSelectedCall] = useState<CallDetailResponse | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    fetchCalls();
    fetchSyncStatus();
  }, [page, debouncedSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lock body scroll when modal is open
  useEffect(() => {
    if (selectedCall) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to ensure scroll is restored
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedCall]);



  const fetchSyncStatus = async () => {
    try {
      const status = await dataSyncApi.getSyncStatus();
      setSyncStatus(status);
    } catch (err) {
      console.error('Failed to fetch sync status:', err);
    }
  };

  const fetchCalls = async () => {
    try {
      setLoading(true);
      setError(null); // Clear previous errors
      const params: any = { page, page_size: 20 };
      if (debouncedSearchTerm.trim()) {
        params.search = debouncedSearchTerm.trim();
      }
      const response = await callsApi.getCalls(params);

      // Backend returns array directly
      if (Array.isArray(response)) {
        safeSetCalls(response);
        setTotalPages(1); // Default to 1 page when no pagination info
        setError(null); // Clear any previous errors since this is valid
      } else {
        console.error('Invalid response structure from API:', response);
        safeSetCalls([]);
        setTotalPages(1);
        setError('Invalid response from API');
      }
    } catch (err) {
      setError('Failed to load calls from database');
      console.error('Database calls error:', err);
      // Ensure calls is still an array even on error
      safeSetCalls([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);

      // Check if we should use API sync or just refresh from database
      if (syncStatus?.configuration.data_source_mode === 'api' && syncStatus.can_refresh) {
        // Use API sync for refresh
        await dataSyncApi.refreshCalls(false);

        // Update sync status
        await fetchSyncStatus();

        // Refresh calls from database
        await fetchCalls();
      } else {
        // Just refresh from database (webhook mode or API not configured)
        await fetchCalls();
      }
    } catch (err) {
      console.error('Refresh failed:', err);
      setError('Refresh failed. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleCallClick = async (call: Call) => {
    try {
      setLoadingDetail(true);
      const detail = await callsApi.getCall(call.id);
      setSelectedCall(detail);
    } catch (err) {
      console.error('Failed to load call detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds: number | null | undefined) => {
    if (seconds === null || seconds === undefined) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return 'N/A';
    return `$${amount.toFixed(2)}`;
  };

  const parseEndedBy = (endedReason: string | null | undefined) => {
    if (!endedReason) return { text: 'Unknown', type: 'unknown' };

    const reason = endedReason.toLowerCase();
    if (reason.includes('customer')) {
      return { text: 'Customer', type: 'customer' };
    } else if (reason.includes('assistant') || reason.includes('agent')) {
      return { text: 'Assistant', type: 'assistant' };
    } else {
      return { text: 'Unknown', type: 'unknown' };
    }
  };

  // 1. Handle loading state
  if (loading && calls.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Call Logs</h1>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading calls...</div>
        </div>
      </div>
    );
  }

  // 2. Handle error state
  if (error && calls.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Call Logs</h1>
          <button
            onClick={handleRefresh}
            disabled={loading || refreshing}
            className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 disabled:opacity-50 ${isDarkMode
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <div className={`border rounded-md p-4 ${isDarkMode
          ? 'bg-red-900/20 border-red-800 text-red-300'
          : 'bg-red-50 border-red-200 text-red-800'
          }`}>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // 3. Ensure calls is always an array before rendering
  // Multiple layers of safety to prevent undefined.map() errors
  let safeCalls: Call[] = [];

  if (calls === null || calls === undefined) {
    safeCalls = [];
  } else if (Array.isArray(calls)) {
    safeCalls = calls;
  } else {
    console.error('Calls is not an array:', typeof calls, calls);
    safeCalls = [];
  }

  // Debug logging


  return (
    <>
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col space-y-4 lg:flex-row lg:items-start lg:justify-between lg:space-y-0">
          <div className="flex-1 min-w-0">
            <h1 className={`text-responsive-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Call Logs</h1>
            <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-x-4 sm:space-y-0 mt-1">
              <p className={`text-responsive-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {syncStatus?.configuration.data_source_mode === 'api'
                  ? `API Sync Mode • Auto-refresh every ${syncStatus.scheduler_status.sync_interval_minutes} min`
                  : 'Webhook Mode • Real-time updates'
                }
              </p>
              {syncStatus?.scheduler_status.last_sync_time && (
                <p className={`text-responsive-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Last sync: {new Date(syncStatus.scheduler_status.last_sync_time).toLocaleString()}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0 lg:flex-shrink-0">
            {/* {syncStatus?.configuration.data_source_mode === 'api' && syncStatus.can_refresh && (
            <div className="flex items-center justify-center sm:justify-start">
              {syncStatus.scheduler_status.next_sync_in_seconds !== null && (
                <span className={`text-responsive-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Next auto-sync in {Math.max(0, syncStatus.scheduler_status.next_sync_in_seconds)}s
                </span>
              )}
            </div>
          )} */}
            <button
              onClick={handleRefresh}
              disabled={loading || refreshing}
              className={`touch-target btn-mobile px-4 py-2 rounded-md font-medium transition-colors duration-200 disabled:opacity-50 flex items-center justify-center space-x-2 ${isDarkMode
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
            >
              {refreshing && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              <span>
                {refreshing
                  ? (syncStatus?.configuration.data_source_mode === 'api' ? 'Syncing...' : 'Refreshing...')
                  : 'Refresh'
                }
              </span>
            </button>
          </div>
        </div>

        {error && (
          <div className={`border rounded-md p-4 ${isDarkMode
            ? 'bg-red-900/20 border-red-800 text-red-300'
            : 'bg-red-50 border-red-200 text-red-800'
            }`}>
            <p>{error}</p>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by caller name or call ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`block w-full pl-10 pr-3 py-2 border rounded-md leading-5 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${isDarkMode
                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900'
                }`}
            />
          </div>
        </div>

        {/* Calls Table */}
        <div className={`shadow rounded-lg overflow-hidden transition-colors duration-200 mobile-scroll ${isDarkMode ? 'bg-gray-800 shadow-black/20' : 'bg-white shadow-gray-200'
          }`}>
          <div className="overflow-x-auto">
            <table className="table-mobile min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={`${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <tr>
                  <th className={`px-3 md:px-6 py-3 text-left text-responsive-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                    Caller
                  </th>
                  <th className={`px-3 md:px-6 py-3 text-left text-responsive-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                    Date & Time
                  </th>
                  <th className={`px-3 md:px-6 py-3 text-left text-responsive-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                    Duration
                  </th>
                  {/* <th className={`px-3 md:px-6 py-3 text-left text-responsive-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                  Cost
                </th> */}
                  <th className={`px-3 md:px-6 py-3 text-left text-responsive-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                    Ended By
                  </th>
                  <th className={`hidden sm:table-cell px-3 md:px-6 py-3 text-left text-responsive-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                    Phone Number
                  </th>
                  <th className={`px-3 md:px-6 py-3 text-left text-responsive-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y transition-colors duration-200 ${isDarkMode
                ? 'bg-gray-800 divide-gray-700'
                : 'bg-white divide-gray-200'
                }`}>
                {safeCalls.map((call) => (
                  <tr key={call.id} className={`transition-colors duration-200 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    }`}>
                    <td className={`px-3 md:px-6 py-3 md:py-4 text-responsive-sm ${isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                      <div className="truncate max-w-[120px] sm:max-w-none">
                        {call.full_name || 'Unknown Caller'}
                      </div>
                    </td>
                    <td className={`px-3 md:px-6 py-3 md:py-4 text-responsive-sm ${isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                      <div className="truncate max-w-[120px] sm:max-w-none">
                        {call.start_time ? formatDate(call.start_time) : 'N/A'}
                      </div>
                    </td>
                    <td className={`px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-responsive-sm ${isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                      {formatDuration(call.duration_seconds)}
                    </td>
                    {/* <td className={`px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-responsive-sm ${isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                    {formatCurrency(call.total_cost)}
                  </td> */}
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                      {(() => {
                        const endedBy = parseEndedBy(call.ended_reason);
                        return (
                          <span className={`inline-flex px-2 py-1 text-responsive-xs font-semibold rounded-full ${endedBy.type === 'assistant'
                            ? 'bg-green-100 text-green-800'
                            : endedBy.type === 'customer'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                            }`}>
                            {endedBy.text}
                          </span>
                        );
                      })()}
                    </td>
                    <td className={`hidden sm:table-cell px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-responsive-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                      {call.phone_number || 'N/A'}
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-responsive-sm font-medium">
                      <button
                        onClick={() => handleCallClick(call)}
                        className={`touch-target transition-colors duration-200 ${isDarkMode
                          ? 'text-blue-400 hover:text-blue-300'
                          : 'text-blue-600 hover:text-blue-900'
                          }`}
                      >
                        <span className="hidden sm:inline">View Details</span>
                        <span className="sm:hidden">View</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {safeCalls.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No calls found</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && safeCalls.length > 0 && (
          <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className={`touch-target border px-4 py-2 rounded-md transition-colors duration-200 disabled:opacity-50 ${isDarkMode
                ? 'bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
            >
              Previous
            </button>
            <span className={`text-responsive-sm text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className={`touch-target border px-4 py-2 rounded-md transition-colors duration-200 disabled:opacity-50 ${isDarkMode
                ? 'bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Call Detail Modal */}
      {selectedCall && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 z-50 overflow-y-auto"
          onClick={() => setSelectedCall(null)}
        >
          <div
            className={`rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto transform ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >

            <div
              className={`sticky top-0 z-10 flex flex-col px-4 py-3 md:px-6 border-b shadow-sm gap-4 ${isDarkMode
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
                }`}
            >
              {/* Row 1: Title and Close Button */}
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  <h2
                    className={`text-responsive-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}
                  >
                    Call Details
                  </h2>
                  <span
                    title="Call ID"
                    className={`text-responsive-sm font-mono px-2 py-1 rounded ${isDarkMode
                      ? 'bg-gray-700 text-gray-200'
                      : 'bg-gray-100 text-gray-700'
                      }`}
                  >
                    {selectedCall.id}
                  </span>
                </div>

                <button
                  onClick={() => setSelectedCall(null)}
                  className={`touch-target p-2 rounded-md transition-colors ${isDarkMode
                    ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                    }`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Row 2: Summary Details */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <h3 className={`text-responsive-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Caller Name
                  </h3>
                  <p className={`text-responsive-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {selectedCall.full_name || 'Unknown Caller'}
                  </p>
                </div>

                <div className="flex-1">
                  <h3 className={`text-responsive-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Phone Number
                  </h3>
                  <p className={`text-responsive-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {selectedCall.phone_number || 'N/A'}
                  </p>
                </div>



                <div className="flex-1">
                  <h3 className={`text-responsive-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Duration
                  </h3>
                  <p className={`text-responsive-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {selectedCall.duration_seconds
                      ? formatDuration(selectedCall.duration_seconds)
                      : 'N/A'}
                  </p>
                </div>

                <div className="flex-1">
                  <h3 className={`text-responsive-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Ended By
                  </h3>
                  <p className={`text-responsive-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {parseEndedBy(selectedCall.ended_reason).text}
                  </p>
                </div>


                {selectedCall.recording_url && (
                  <div className="flex-1">
                    <button
                      onClick={async (e) => {
                        e.preventDefault();
                        if (selectedCall.recording_url) {
                          try {
                            const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
                            
                            let fetchUrl = selectedCall.recording_url;
                            // Only prepend API_BASE_URL if it's a relative path from the proxy
                            if (!fetchUrl.startsWith('http')) {
                              fetchUrl = `${API_BASE_URL}${fetchUrl.startsWith('/') ? '' : '/'}${fetchUrl}`;
                            }


                            const response = await fetch(fetchUrl, {
                              headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                              }
                            });
                            if (!response.ok) {
                              let errorMsg = `Failed to fetch recording (${response.status})`;
                              try {
                                const errData = await response.json();
                                errorMsg = errData.detail || errorMsg;
                              } catch (e) { }
                              throw new Error(errorMsg);
                            }
                            const contentType = response.headers.get('content-type');
                            let blob: Blob;
                            
                            if (contentType && contentType.includes('application/json')) {
                              const data = await response.json();
                              if (data.url) {
                                // Fetch without our backend's Authorization header to prevent S3 400 Bad Request
                                const audioResponse = await fetch(data.url);
                                if (!audioResponse.ok) {
                                  throw new Error(`Failed to download audio from signed URL (${audioResponse.status})`);
                                }
                                blob = await audioResponse.blob();
                              } else {
                                throw new Error('Invalid JSON response format from server (missing url)');
                              }
                            } else {
                              blob = await response.blob();
                            }
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `call-recording-${selectedCall.id}.wav`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                          } catch (error: any) {
                            console.error('Download failed:', error);
                            alert('This recording is no longer available.');
                          }
                        }
                      }}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                    >
                      Download Recording
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 space-y-6">

              {loadingDetail ? (
                <div className="text-center py-8">
                  <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading call details...</div>
                </div>
              ) : (
                <div className="space-y-4 md:space-y-6">
                  {/* Call Summary */}





                  {/* Summary */}
                  {selectedCall.summary && (
                    <div>
                      <h3 className={`text-lg font-medium mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Summary</h3>

                      <p className="text-gray-700 bg-gray-50 p-4 rounded-md">{selectedCall.summary}</p>
                    </div>
                  )}

                  {/* Transcript */}
                  <div>
                    <h3 className={`text-lg font-medium mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Transcript</h3>
                    {selectedCall.messages.length > 0 ? (
                      <div className="space-y-4">
                        {selectedCall.messages.map((message, index) => {
                          // Filter out system messages/prompts from the transcript view
                          if (message.role === 'system') return null;

                          return (
                            <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                              <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${message.role === 'user'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-900'
                                }`}>
                                <div className="text-xs opacity-75 mb-1">
                                  {message.role === 'user' ? 'User' : 'Assistant'}
                                  {/* Fix for 0s timestamp display issue */}
                                  {message.seconds_from_start !== undefined && message.seconds_from_start !== null && ` • ${Math.floor(message.seconds_from_start)}s`}
                                </div>
                                <div>{message.content}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : selectedCall.full_transcript ? (
                      <div className="bg-gray-50 p-4 rounded-md">
                        <p className="text-gray-700 whitespace-pre-wrap">{selectedCall.full_transcript}</p>
                      </div>
                    ) : (
                      <p className="text-gray-500">No transcript available</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Calls;